import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { DollarSign, TrendingUp, TrendingDown, Calendar, Plus, List, Package, AlertTriangle, Download } from 'lucide-react';
import { stats as statsApi } from '../utils/api';
import { format } from 'date-fns';
import { parseLocalDate } from '../utils/timeFormat';
import ManualPaymentEntryModal from '../components/ManualPaymentEntryModal';
import PaymentLogsViewer from '../components/PaymentLogsViewer';
import TrafficAnalyticsCard from '../components/TrafficAnalyticsCard';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Stats = () => {
  const [period, setPeriod] = useState('monthly'); // weekly, monthly, yearly
  const [earnings, setEarnings] = useState(null);
  const [allTimeStats, setAllTimeStats] = useState(null);
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [showPaymentLogsViewer, setShowPaymentLogsViewer] = useState(false);
  const [inventoryStats, setInventoryStats] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [earningsRes, allTimeRes, clientsRes, inventoryRes] = await Promise.all([
        statsApi.getEarnings(period),
        statsApi.getAllTime(),
        statsApi.getTopClients(10),
        statsApi.getInventory().catch(() => ({ data: null })),
      ]);

      setEarnings(earningsRes.data);
      setAllTimeStats(allTimeRes.data);
      setTopClients(clientsRes.data);
      setInventoryStats(inventoryRes.data);
    } catch (error) {
      console.error('Load stats error:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const handleDownloadReport = () => {
    const lines = [];
    const now = new Date().toLocaleDateString();
    lines.push(`Stats Report - ${period.charAt(0).toUpperCase() + period.slice(1)} - Generated ${now}`);
    lines.push('');

    // Revenue Summary
    lines.push('=== REVENUE SUMMARY ===');
    lines.push(`Net Revenue,${earnings?.summary?.net_revenue || 0}`);
    lines.push(`Gross Revenue,${earnings?.summary?.gross_revenue || 0}`);
    lines.push(`Total Discounts,${earnings?.summary?.total_discounts || 0}`);
    lines.push(`Total Refunds,${earnings?.summary?.total_refunds || 0}`);
    lines.push(`Completed Appointments,${earnings?.summary?.total_appointments || 0}`);
    lines.push('');

    // All-Time Stats
    lines.push('=== ALL-TIME STATISTICS ===');
    lines.push(`Lifetime Revenue,${allTimeStats?.lifetime_revenue || 0}`);
    lines.push(`Total Collected,${allTimeStats?.total_collected || 0}`);
    lines.push(`Lifetime Discounts,${allTimeStats?.lifetime_discounts || 0}`);
    lines.push(`Lifetime Refunds,${allTimeStats?.lifetime_refunds || 0}`);
    lines.push(`Total Clients,${allTimeStats?.total_clients || 0}`);
    lines.push(`Total Pets,${allTimeStats?.total_pets || 0}`);
    lines.push(`Paid Appointments,${allTimeStats?.paid_appointments || 0}`);
    lines.push(`Unpaid Appointments,${allTimeStats?.unpaid_appointments || 0}`);
    lines.push('');

    // Revenue by Service
    if (earnings?.serviceBreakdown?.length > 0) {
      lines.push('=== REVENUE BY SERVICE ===');
      lines.push('Service,Bookings,Revenue');
      earnings.serviceBreakdown.forEach(s => {
        lines.push(`"${s.service_name}",${s.booking_count},${s.revenue}`);
      });
      lines.push('');
    }

    // Top Clients
    if (topClients.length > 0) {
      lines.push('=== TOP CLIENTS ===');
      lines.push('Client,Email,Appointments,Total Spent');
      topClients.forEach(c => {
        lines.push(`"${c.first_name} ${c.last_name}",${c.email},${c.total_appointments},${c.total_spent}`);
      });
      lines.push('');
    }

    // Payment Method Breakdown
    if (earnings?.paymentBreakdown?.length > 0) {
      lines.push('=== PAYMENT METHOD BREAKDOWN ===');
      lines.push('Payment Method,Transactions,Total Collected,Total Refunded,Net');
      earnings.paymentBreakdown.forEach(m => {
        const net = parseFloat(m.total_collected) - parseFloat(m.total_refunded);
        lines.push(`"${m.payment_method || 'Unpaid'}",${m.transaction_count},${m.total_collected},${m.total_refunded},${net.toFixed(2)}`);
      });
      lines.push('');
    }

    // Inventory
    if (inventoryStats) {
      lines.push('=== INVENTORY OVERVIEW ===');
      lines.push(`Total Items,${inventoryStats.summary?.total_items || 0}`);
      lines.push(`Total Units,${inventoryStats.summary?.total_units || 0}`);
      lines.push(`Inventory Value,${inventoryStats.summary?.total_value || 0}`);
      lines.push(`Low Stock Items,${inventoryStats.summary?.low_stock_count || 0}`);
      lines.push(`Out of Stock Items,${inventoryStats.summary?.out_of_stock_count || 0}`);
      lines.push('');

      if (inventoryStats.categoryBreakdown?.length > 0) {
        lines.push('=== INVENTORY BY CATEGORY ===');
        lines.push('Category,Items,Units,Value');
        inventoryStats.categoryBreakdown.forEach(cat => {
          lines.push(`"${cat.category}",${cat.item_count},${cat.total_units},${cat.total_value}`);
        });
        lines.push('');
      }

      if (inventoryStats.lowStockItems?.length > 0) {
        lines.push('=== LOW STOCK ALERTS ===');
        lines.push('Item,Category,Quantity,Reorder Level,Cost Per Unit');
        inventoryStats.lowStockItems.forEach(item => {
          lines.push(`"${item.item_name}","${item.category || ''}",${item.quantity},${item.reorder_level},${item.cost_per_unit || 0}`);
        });
        lines.push('');
      }
    }

    // Daily Breakdown
    if (earnings?.dailyBreakdown?.length > 0) {
      lines.push('=== DAILY REVENUE BREAKDOWN ===');
      lines.push('Date,Revenue,Appointments');
      earnings.dailyBreakdown.forEach(day => {
        lines.push(`${day.appointment_date},${day.daily_revenue},${day.appointments_count}`);
      });
    }

    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `stats-report-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('Report downloaded!');
  };

  const handleManualPayment = async (paymentData) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(`${API_URL}/payments/manual-payment`, paymentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reload stats to reflect new payment
      loadData();
    } catch (error) {
      console.error('Manual payment error:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Earnings & Revenue Analytics</h1>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDownloadReport}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <Download size={20} />
              <span>Download Report</span>
            </button>
            <button
              onClick={() => setShowPaymentLogsViewer(true)}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <List size={20} />
              <span>View Payment History</span>
            </button>
            <button
              onClick={() => setShowManualPaymentModal(true)}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus size={20} />
              <span>Record Payment</span>
            </button>
          </div>
        </div>

        <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex justify-end space-x-2">
              {['weekly', 'monthly', 'yearly'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    period === p
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>

            {/* Summary Cards */}
            {/* Traffic Analytics — public-page views, top services, referrers,
                conversion rate. Independent of revenue / appointment stats. */}
            <TrafficAnalyticsCard />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Net Revenue</h3>
                  <DollarSign className="text-green-600" size={24} />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(earnings?.summary?.net_revenue)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {earnings?.summary?.total_appointments} completed appointments
                </p>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Gross Revenue</h3>
                  <TrendingUp className="text-blue-600" size={24} />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(earnings?.summary?.gross_revenue)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Before discounts & refunds</p>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Discounts</h3>
                  <TrendingDown className="text-orange-600" size={24} />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(earnings?.summary?.total_discounts)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Given to clients</p>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Refunds</h3>
                  <TrendingDown className="text-red-600" size={24} />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(earnings?.summary?.total_refunds)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Issued to clients</p>
              </div>
            </div>

            {/* All-Time Stats */}
            <div className="card">
              <h2 className="text-xl font-bold mb-4">All-Time Statistics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Lifetime Revenue</p>
                  <p className="text-2xl font-bold text-primary-600">
                    {formatCurrency(allTimeStats?.lifetime_revenue)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Collected</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(allTimeStats?.total_collected)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Lifetime Discounts</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(allTimeStats?.lifetime_discounts)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Lifetime Refunds</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(allTimeStats?.lifetime_refunds)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-600">Total Clients</p>
                  <p className="text-2xl font-bold text-gray-900">{allTimeStats?.total_clients}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Pets</p>
                  <p className="text-2xl font-bold text-gray-900">{allTimeStats?.total_pets}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Paid Appointments</p>
                  <p className="text-2xl font-bold text-green-900">{allTimeStats?.paid_appointments}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Unpaid Appointments</p>
                  <p className="text-2xl font-bold text-orange-900">{allTimeStats?.unpaid_appointments}</p>
                </div>
              </div>
            </div>

            {/* Revenue by Service */}
            <div className="card">
              <h2 className="text-xl font-bold mb-4">Revenue by Service</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4">Service</th>
                      <th className="text-right py-3 px-4">Bookings</th>
                      <th className="text-right py-3 px-4">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings?.serviceBreakdown?.map((service, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-3 px-4">{service.service_name}</td>
                        <td className="text-right py-3 px-4">{service.booking_count}</td>
                        <td className="text-right py-3 px-4 font-semibold">
                          {formatCurrency(service.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top Clients */}
            <div className="card">
              <h2 className="text-xl font-bold mb-4">Top Clients by Revenue</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4">Client</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-right py-3 px-4">Appointments</th>
                      <th className="text-right py-3 px-4">Total Spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topClients.map((client, index) => (
                      <tr key={client.id} className="border-b border-gray-100">
                        <td className="py-3 px-4">
                          {client.first_name} {client.last_name}
                        </td>
                        <td className="py-3 px-4 text-gray-600">{client.email}</td>
                        <td className="text-right py-3 px-4">{client.total_appointments}</td>
                        <td className="text-right py-3 px-4 font-semibold">
                          {formatCurrency(client.total_spent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment Method Breakdown */}
            {earnings?.paymentBreakdown && earnings.paymentBreakdown.length > 0 && (
              <div className="card">
                <h2 className="text-xl font-bold mb-4">Payment Method Breakdown</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4">Payment Method</th>
                        <th className="text-right py-3 px-4">Transactions</th>
                        <th className="text-right py-3 px-4">Total Collected</th>
                        <th className="text-right py-3 px-4">Total Refunded</th>
                        <th className="text-right py-3 px-4">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earnings.paymentBreakdown.map((method, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="py-3 px-4 capitalize">
                            {method.payment_method === 'cash_only' ? 'Pay in Person' : method.payment_method || 'Unpaid'}
                          </td>
                          <td className="text-right py-3 px-4">{method.transaction_count}</td>
                          <td className="text-right py-3 px-4 text-green-600 font-semibold">
                            {formatCurrency(method.total_collected)}
                          </td>
                          <td className="text-right py-3 px-4 text-red-600">
                            {formatCurrency(method.total_refunded)}
                          </td>
                          <td className="text-right py-3 px-4 font-bold">
                            {formatCurrency(parseFloat(method.total_collected) - parseFloat(method.total_refunded))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Inventory Overview */}
            {inventoryStats && (
              <div className="card">
                <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
                  <Package size={24} className="text-primary-600" />
                  <span>Inventory Overview</span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Items</p>
                    <p className="text-2xl font-bold text-gray-900">{inventoryStats.summary?.total_items || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Units</p>
                    <p className="text-2xl font-bold text-gray-900">{inventoryStats.summary?.total_units || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Inventory Value</p>
                    <p className="text-2xl font-bold text-primary-600">{formatCurrency(inventoryStats.summary?.total_value)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Low Stock</p>
                    <p className={`text-2xl font-bold ${parseInt(inventoryStats.summary?.low_stock_count) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {inventoryStats.summary?.low_stock_count || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Out of Stock</p>
                    <p className={`text-2xl font-bold ${parseInt(inventoryStats.summary?.out_of_stock_count) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {inventoryStats.summary?.out_of_stock_count || 0}
                    </p>
                  </div>
                </div>

                {/* Low Stock Alerts */}
                {inventoryStats.lowStockItems?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h3 className="font-semibold text-orange-700 flex items-center space-x-1 mb-3">
                      <AlertTriangle size={18} />
                      <span>Low Stock Alerts</span>
                    </h3>
                    <div className="space-y-2">
                      {inventoryStats.lowStockItems.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                          <div>
                            <span className="font-medium text-gray-900">{item.item_name}</span>
                            {item.category && <span className="text-sm text-gray-500 ml-2">({item.category})</span>}
                          </div>
                          <div className="text-right">
                            <span className={`font-bold ${item.quantity === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                              {item.quantity} left
                            </span>
                            <span className="text-sm text-gray-500 ml-1">(reorder at {item.reorder_level})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category Breakdown */}
                {inventoryStats.categoryBreakdown?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h3 className="font-semibold mb-3">Inventory by Category</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-4">Category</th>
                            <th className="text-right py-2 px-4">Items</th>
                            <th className="text-right py-2 px-4">Units</th>
                            <th className="text-right py-2 px-4">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryStats.categoryBreakdown.map((cat, index) => (
                            <tr key={index} className="border-b border-gray-100">
                              <td className="py-2 px-4">{cat.category}</td>
                              <td className="text-right py-2 px-4">{cat.item_count}</td>
                              <td className="text-right py-2 px-4">{cat.total_units}</td>
                              <td className="text-right py-2 px-4 font-semibold">{formatCurrency(cat.total_value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Daily Breakdown */}
            <div className="card">
              <h2 className="text-xl font-bold mb-4">Daily Revenue Breakdown</h2>
              <div className="space-y-2">
                {earnings?.dailyBreakdown?.map((day, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Calendar size={20} className="text-gray-600" />
                      <span className="font-medium">{format(parseLocalDate(day.appointment_date), 'MMM d, yyyy')}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary-600">{formatCurrency(day.daily_revenue)}</p>
                      <p className="text-sm text-gray-600">{day.appointments_count} appointments</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
        </div>
      </div>

      {/* Manual Payment Entry Modal */}
      <ManualPaymentEntryModal
        isOpen={showManualPaymentModal}
        onClose={() => setShowManualPaymentModal(false)}
        onSuccess={handleManualPayment}
      />

      {/* Payment Logs Viewer */}
      <PaymentLogsViewer
        isOpen={showPaymentLogsViewer}
        onClose={() => setShowPaymentLogsViewer(false)}
        onUpdate={loadData}
      />
    </div>
  );
};

export default Stats;
